import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import ArchivedRmaCard from './ArchivedRmaCard.jsx';
import LoadingSpinner from './LoadingSpinner';
import { colors } from '../theme';
import useRmaPdfLocale from '../hooks/useRmaPdfLocale';

const skeletonStyle = {
  height: '160px',
  borderRadius: '8px',
  background: 'linear-gradient(90deg, #f5f5f5 25%, #ececec 37%, #f5f5f5 63%)',
  backgroundSize: '400% 100%',
  animation: 'archivedSkeletonPulse 1.4s ease-in-out infinite'
};

const skeletonContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem'
};

export default function ArchivedRmaList({
  items,
  isLoading,
  error,
  onRetry,
  emptyMessage = 'No archived RMAs found.',
  showPdfLink = true,
  highlightRmaId = null
}) {
  const [pdfLocale, setPdfLocale] = useRmaPdfLocale();
  const highlightRef = useRef(null);

  useEffect(() => {
    if (highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [items, highlightRmaId]);

  if (isLoading) {
    return (
      <div role="status" aria-live="polite" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center' }}>
        <div style={skeletonContainerStyle}>
          <div style={skeletonStyle} />
          <div style={skeletonStyle} />
          <div style={skeletonStyle} />
        </div>
        <LoadingSpinner size={40} />
        <p style={{ color: colors.textMuted, margin: 0 }}>Loading archived RMAs…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div role="alert" style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        backgroundColor: colors.background,
        borderRadius: '8px',
        border: `1px solid ${colors.accent}`,
        padding: '1rem'
      }}>
        <p style={{ margin: 0, color: colors.textMuted }}>
          Unable to load archived RMAs. {error.message || 'Please try again.'}
        </p>
        {typeof onRetry === 'function' && (
          <button
            type="button"
            onClick={onRetry}
            style={{
              alignSelf: 'flex-start',
              backgroundColor: colors.accent,
              color: colors.background,
              border: 'none',
              borderRadius: '4px',
              padding: '0.5rem 1rem',
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <p style={{ color: colors.textMuted }}>{emptyMessage}</p>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {items.map(rma => {
        const isHighlighted = highlightRmaId && rma.rmaId === highlightRmaId;
        return (
          <div key={rma._id || rma.rmaId} ref={isHighlighted ? highlightRef : null}>
            <ArchivedRmaCard
              rma={rma}
              showPdfLink={showPdfLink}
              pdfLocale={pdfLocale}
              onChangePdfLocale={setPdfLocale}
              highlighted={isHighlighted}
            />
          </div>
        );
      })}
    </div>
  );
}

ArchivedRmaList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  error: PropTypes.object,
  onRetry: PropTypes.func,
  emptyMessage: PropTypes.string,
  showPdfLink: PropTypes.bool,
  highlightRmaId: PropTypes.string
};
